import type { CSSProperties } from 'react';
import type { FamilyMember, MemberColor } from '@app/shared';

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts.length === 1 ? parts[0]!.slice(0, 2) : `${parts[0]![0]}${parts[parts.length - 1]![0]}`).toUpperCase();
}

/** CSS variables for a member's color, from theme tokens. */
export function memberStyle(color: MemberColor | undefined): CSSProperties {
  const c = color ?? 'slate';
  return {
    '--av': `var(--member-${c})`,
    '--on-av': `var(--on-member-${c})`,
    '--m': `var(--member-${c})`,
    '--on-m': `var(--on-member-${c})`,
    '--m-soft': `var(--member-${c}-soft)`,
    '--on-m-soft': `var(--on-member-${c}-soft)`,
  } as CSSProperties;
}

export function Avatar({
  member,
  name,
  color,
  size = 'md',
}: {
  member?: Pick<FamilyMember, 'displayName' | 'color'>;
  name?: string;
  color?: MemberColor;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const displayName = member?.displayName ?? name ?? '?';
  return (
    <span className={`ui-avatar${size !== 'md' ? ` ui-avatar--${size}` : ''}`} style={memberStyle(member?.color ?? color)} aria-hidden="true">
      {initials(displayName)}
    </span>
  );
}

/** Toggle chips for choosing family members (assignees, participants, sharing). */
export function MemberPicker({
  members,
  selected,
  onChange,
  multiple = true,
  label,
  allowNone,
  noneLabel = 'Anyone',
}: {
  members: FamilyMember[];
  selected: string[];
  onChange(ids: string[]): void;
  multiple?: boolean;
  label: string;
  allowNone?: boolean;
  noneLabel?: string;
}) {
  return (
    <div className="ui-chip-group" role="group" aria-label={label}>
      {allowNone && !multiple && (
        <button type="button" className="ui-chip ui-chip--plain" aria-pressed={selected.length === 0} onClick={() => onChange([])}>
          {noneLabel}
        </button>
      )}
      {members.map((m) => {
        const on = selected.includes(m.userId);
        return (
          <button
            key={m.userId}
            type="button"
            className="ui-chip"
            aria-pressed={on}
            onClick={() => {
              if (multiple) onChange(on ? selected.filter((id) => id !== m.userId) : [...selected, m.userId]);
              else onChange(on && allowNone ? [] : [m.userId]);
            }}
          >
            <Avatar member={m} size="sm" />
            {m.displayName}
          </button>
        );
      })}
    </div>
  );
}
